import React, { useState } from "react";
import styled from "styled-components";

import colors from "@/design/colors";
import fonts from "@/design/typography";

export interface EpayCard {
  id: number;
  nickname: string;
  last4: string;
  cardCompanyCode: string;
  cardCompanyName: string;
  cardType: string;
  cardImageUrl?: string;
  offlineAvailable: boolean;
  defaultMethod: boolean;
}

export const parseEpayReturn = (search: string) => {
  const params = new URLSearchParams(search);
  return {
    step: params.get("epay"),
    code: params.get("epayCode"),
    message: params.get("epayMessage"),
  };
};

export const isSuccessfulEpayPaymentReturn = (search: string) => {
  const result = parseEpayReturn(search);
  return result.step === "payment" && result.code === "0000";
};

// 결제할 수 있으면 기본 카드, 없으면 결제 가능한 첫 카드.
export const defaultEpayCardId = (cards: EpayCard[], paymentEnabled: boolean) => {
  if (!paymentEnabled) return null;
  const card = cards.find((c) => c.offlineAvailable && c.defaultMethod) || cards.find((c) => c.offlineAvailable);
  return card ? card.id : null;
};

export const EPAY_CARD_REGISTERED_MESSAGE = "카드 등록이 끝났어요. 결제 가능 여부는 아래 카드 목록에서 확인해주세요.";

// 안내 전용. 실제 토큰 서명과 만료 여부는 서버가 검증한다.
export const isEpayLinkExpired = (search: string, now = Date.now()) => {
  const token = new URLSearchParams(search).get("accessToken") || "";
  const match = /^v1\.(\d+)\.[0-9a-f]{64}$/.exec(token);
  return !!match && Number(match[1]) <= Math.floor(now / 1000);
};

// 저장 카드 결제에 쓸 접근 토큰이 없거나(청구서 링크) 만료됐는지(30분 지난 링크). 안내 전용 — 검증은 서버가 한다.
export const needsEpayPhoneCheck = (search: string, now = Date.now()) => {
  const token = new URLSearchParams(search).get("accessToken");
  return !token || isEpayLinkExpired(search, now);
};

/**
 * 받는 분 휴대폰 뒷자리 4자리를 확인받고 저장 카드(카리페이) 결제가 열리는 새 결제창으로 간다.
 * 토큰 없는 청구서 링크(카리페이 콘솔·카리아카 알림톡)와 30분 지난 링크(호두랩스 문자 등)에서만 보인다.
 */
export const EpayPhoneCheck = ({
  busy,
  error,
  onSubmit,
}: {
  busy: boolean;
  error: string;
  onSubmit: (phoneLast4: string) => void;
}) => {
  const [digits, setDigits] = useState("");
  return (
    <EpayPhoneCheckBox
      as="form"
      aria-label="카드등록 결제 본인 확인"
      onSubmit={(e: React.FormEvent) => {
        e.preventDefault();
        if (/^\d{4}$/.test(digits) && !busy) onSubmit(digits);
      }}
    >
      <EpayPhoneCheckTitle>카드등록으로 결제하기</EpayPhoneCheckTitle>
      <EpayCaption>
        받는 분 휴대폰 번호 뒷자리 4자리를 입력하면 카드등록 결제를 열어 드려요. 카드를 한 번 등록해 두면 다음부터 결제 비밀번호만으로 결제할 수 있어요.
      </EpayCaption>
      <EpayPhoneCheckRow>
        <EpayPhoneCheckInput
          aria-label="휴대폰 번호 뒷자리 4자리"
          inputMode="numeric"
          autoComplete="off"
          maxLength={4}
          placeholder="0000"
          value={digits}
          aria-invalid={!!error}
          onChange={(e) => setDigits(e.target.value.replace(/\D/g, "").slice(0, 4))}
        />
        <EpayPhoneCheckButton type="submit" disabled={busy || digits.length !== 4}>
          {busy ? "확인 중" : "확인"}
        </EpayPhoneCheckButton>
      </EpayPhoneCheckRow>
      {error && <EpayPhoneCheckError role="alert">{error}</EpayPhoneCheckError>}
    </EpayPhoneCheckBox>
  );
};

export const EpayExpiredLinkNotice = () => (
  <EpayNotice role="status">
    저장한 카드로 결제할 수 있는 시간이 지났어요. 청구서를 보낸 업체에 재발송을 요청해주세요. 아래 앱 카드 결제는 계속 이용할 수 있어요.
  </EpayNotice>
);

export const LinkPayCardNotice = () => (
  <EpayCaption role="note">
    법인카드는 카드사 정책에 따라 결제가 제한될 수 있어요. 무기명 법인카드는 카드사에 온라인 결제 이용 등록을 확인해주세요.
  </EpayCaption>
);

// 카드 이미지가 없거나 못 불러올 때 쓰는 카드사 색 배지
const CARD_BRANDS: Array<[RegExp, string, string, string]> = [
  [/신한/, "신한", "#0046FF", "#FFFFFF"],
  [/국민|KB/i, "KB", "#FFBC00", "#3B3B3B"],
  [/현대/, "현대", "#1B1B1B", "#FFFFFF"],
  [/삼성/, "삼성", "#1428A0", "#FFFFFF"],
  [/롯데/, "롯데", "#E60012", "#FFFFFF"],
  [/하나/, "하나", "#00857A", "#FFFFFF"],
  [/비씨|BC/i, "BC", "#F03C3C", "#FFFFFF"],
  [/우리/, "우리", "#0067AC", "#FFFFFF"],
  [/농협|NH/i, "NH", "#00A651", "#FFFFFF"],
  [/씨티|CITI/i, "씨티", "#003B70", "#FFFFFF"],
  [/카카오/, "카카오", "#FEE500", "#191919"],
];

// NICE 가 카드 등록 때 준 카드사 상품 이미지(CARD_IMG_URL). 세로형 카드는 세로 틀로 그대로 보여 준다.
// 결제 링크 주소(접근 토큰 포함)가 카드사 서버로 가지 않게 리퍼러를 보내지 않는다.
const CardArt = ({ card }: { card: EpayCard }) => {
  const [broken, setBroken] = useState(false);
  const [vertical, setVertical] = useState(false);
  if (card.cardImageUrl && !broken) {
    return (
      <CardArtFrame aria-hidden="true" $vertical={vertical}>
        <CardArtImage
          src={card.cardImageUrl}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          onLoad={(event) => setVertical(event.currentTarget.naturalHeight > event.currentTarget.naturalWidth)}
          onError={() => setBroken(true)}
        />
      </CardArtFrame>
    );
  }
  const brand = CARD_BRANDS.filter(([pattern]) => pattern.test(card.cardCompanyName))[0];
  return (
    <CardBrandMark aria-hidden="true" $bg={brand ? brand[2] : colors.gray100} $fg={brand ? brand[3] : colors.gray600}>
      {brand ? brand[1] : card.cardCompanyName.slice(0, 2)}
    </CardBrandMark>
  );
};

const cardMeta = (card: EpayCard) =>
  [card.nickname !== card.cardCompanyName ? card.cardCompanyName : null, card.cardType, `•••• ${card.last4}`]
    .filter(Boolean)
    .join(" · ");

export const EpayCardPanel = ({
  cards,
  selectedCardId,
  paymentEnabled,
  appCardRegistrationEnabled = false,
  authOnlyTest = false,
  busy,
  onSelect,
  onRegister,
  onMakeDefault,
  onDelete,
  onChangePin,
  onViewTerms,
  onWithdraw,
}: {
  cards: EpayCard[];
  selectedCardId: number | null;
  paymentEnabled: boolean;
  appCardRegistrationEnabled?: boolean;
  authOnlyTest?: boolean;
  busy: boolean;
  onSelect: (id: number) => void;
  onRegister: () => void;
  onMakeDefault: (id: number) => void;
  onDelete: (id: number) => void;
  onChangePin: () => void;
  onViewTerms: () => void;
  onWithdraw: () => void;
}) => (
  <EpaySection>
    {!paymentEnabled && (
      <EpayNotice role="status">
        카드 등록·관리는 가능하지만 아직 등록한 카드로 결제는 사용할 수 없어요. 결제하려면 ‘앱 카드’를 선택해주세요.
      </EpayNotice>
    )}
    {authOnlyTest && (
      <EpayNotice role="status">
        개발 서버예요. 결제비밀번호 인증까지만 확인하고 승인(출금)은 하지 않아요.
      </EpayNotice>
    )}

    {cards.length > 0 && (
      <EpayCardList role="radiogroup" aria-label="등록된 결제 카드">
        {cards.map((card) => {
          const selectable = !busy && paymentEnabled && card.offlineAvailable;
          return (
            <EpayCardItem
              key={card.id}
              role="radio"
              tabIndex={selectable ? 0 : -1}
              aria-checked={selectedCardId === card.id}
              aria-disabled={!selectable}
              $selected={selectedCardId === card.id}
              $disabled={!selectable}
              onClick={() => selectable && onSelect(card.id)}
              onKeyDown={(event) => {
                if (selectable && (event.key === "Enter" || event.key === " ")) {
                  event.preventDefault();
                  onSelect(card.id);
                }
              }}
            >
              <CardArt card={card} />
              <EpayCardText>
                <EpayCardName>
                  {card.nickname}
                  {card.defaultMethod && <EpayDefaultBadge>기본</EpayDefaultBadge>}
                </EpayCardName>
                <EpayCardMeta>
                  {cardMeta(card)}
                  {!card.offlineAvailable && " · 현재 결제 미지원"}
                </EpayCardMeta>
              </EpayCardText>
              <EpayCardActions>
                {!card.defaultMethod && (
                  <EpayTextButton
                    type="button"
                    disabled={busy}
                    onClick={(event) => {
                      event.stopPropagation();
                      onMakeDefault(card.id);
                    }}
                  >
                    기본으로
                  </EpayTextButton>
                )}
                <EpayTextButton
                  type="button"
                  disabled={busy}
                  onClick={(event) => {
                    event.stopPropagation();
                    onDelete(card.id);
                  }}
                >
                  삭제
                </EpayTextButton>
              </EpayCardActions>
            </EpayCardItem>
          );
        })}
      </EpayCardList>
    )}

    <EpayAddCard type="button" onClick={onRegister} disabled={busy}>
      <span aria-hidden="true">+</span>
      {busy ? "연결하고 있어요" : "카드 등록하기"}
    </EpayAddCard>

    {cards.length === 0 && (
      <EpayCaption>본인인증과 카드 등록은 NICE에서 진행하고, 카드 정보는 NICE ePAY가 암호화해 관리해요.</EpayCaption>
    )}
    {appCardRegistrationEnabled && (
      <EpayCaption>‘내카드 불러오기’를 사용할 수 있어요. 현재 비씨카드는 불러오기 대상에서 제외돼요.</EpayCaption>
    )}

    <EpayFooter>
      <EpayManagementActions aria-label="카리페이 관리">
        <EpayManagementButton type="button" onClick={onViewTerms} disabled={busy}>
          약관 보기
        </EpayManagementButton>
        <EpayManagementButton type="button" onClick={onChangePin} disabled={busy}>
          결제비밀번호 변경
        </EpayManagementButton>
        <EpayManagementButton type="button" onClick={onWithdraw} disabled={busy}>
          간편결제 해지
        </EpayManagementButton>
      </EpayManagementActions>
      <EpayCaption>카드번호 전체를 카리페이에 저장하지 않아요.</EpayCaption>
    </EpayFooter>
  </EpaySection>
);

// 청구서 화면(BillConfirmPage)의 결제 방법 타일·선택 상자와 같은 규칙: 흰 바탕, gray100 테두리, 8px 모서리,
// 선택 시 blue600 테두리 + blue50 바탕, 글꼴은 typography 토큰.
const EpaySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;

  animation: slideFadeIn 0.4s cubic-bezier(0.5, 1.4, 0.5, 1);
  transform-origin: top;

  @keyframes slideFadeIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const EpayNotice = styled.div`
  padding: 12px 14px;
  border-radius: 8px;
  background-color: ${colors.gray50};
  color: ${colors.gray600};
  font-family: ${fonts.detail2Regular.fontFamily};
  line-height: ${fonts.detail2Regular.lineHeight};
  font-size: ${fonts.detail2Regular.fontSize};
`;

const EpayPhoneCheckBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
  padding: 14px;
  border: 1px solid ${colors.gray100};
  border-radius: 8px;
  background-color: ${colors.white};
`;

const EpayPhoneCheckTitle = styled.div`
  color: ${colors.gray900};
  font-family: ${fonts.body3SemiBold.fontFamily};
  line-height: ${fonts.body3SemiBold.lineHeight};
  font-size: ${fonts.body3SemiBold.fontSize};
`;

const EpayPhoneCheckRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const EpayPhoneCheckInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 48px;
  padding: 0 12px;
  box-sizing: border-box;
  border: 1px solid ${colors.gray200};
  border-radius: 8px;
  color: ${colors.gray900};
  font-size: 18px;
  letter-spacing: 0.4em;
  text-align: center;

  &:focus {
    outline: none;
    border-color: ${colors.blue600};
  }
`;

const EpayPhoneCheckButton = styled.button`
  flex-shrink: 0;
  height: 48px;
  padding: 0 18px;
  border: 0;
  border-radius: 8px;
  background-color: ${colors.blue600};
  color: ${colors.white};
  font-family: ${fonts.body3SemiBold.fontFamily};
  font-size: ${fonts.body3SemiBold.fontSize};
  cursor: pointer;

  &:disabled {
    background-color: ${colors.gray100};
    color: ${colors.gray500};
    cursor: default;
  }
`;

const EpayPhoneCheckError = styled.div`
  margin-top: 4px;
  color: ${colors.red600};
  font-family: ${fonts.detail2Medium.fontFamily};
  line-height: ${fonts.detail2Medium.lineHeight};
  font-size: ${fonts.detail2Medium.fontSize};
`;

const EpayCaption = styled.div`
  margin-top: 4px;
  color: ${colors.gray500};
  font-family: ${fonts.detail2Medium.fontFamily};
  line-height: ${fonts.detail2Medium.lineHeight};
  font-size: ${fonts.detail2Medium.fontSize};
`;

const EpayCardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EpayCardItem = styled.div<{ $selected: boolean; $disabled: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 72px;
  padding: 12px 14px;
  box-sizing: border-box;
  border: ${({ $selected }) => ($selected ? "1.4px solid" : "1px solid")};
  border-color: ${({ $selected }) => ($selected ? colors.blue600 : colors.gray100)};
  border-radius: 8px;
  background-color: ${({ $selected }) => ($selected ? colors.blue50 : colors.white)};
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  opacity: ${({ $disabled, $selected }) => ($disabled && !$selected ? 0.6 : 1)};
`;

// 신용카드 비율(약 1.59:1) 틀. 이미지가 세로형이면 틀도 세로로 세운다.
const CardArtFrame = styled.span<{ $vertical: boolean }>`
  display: block;
  flex: 0 0 auto;
  width: ${({ $vertical }) => ($vertical ? "30px" : "48px")};
  height: ${({ $vertical }) => ($vertical ? "48px" : "30px")};
  margin: ${({ $vertical }) => ($vertical ? "0 9px" : "0")};
  overflow: hidden;
  border-radius: 4px;
  background-color: ${colors.gray50};
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06);
`;

const CardArtImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const CardBrandMark = styled.span<{ $bg: string; $fg: string }>`
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 48px;
  height: 30px;
  border-radius: 4px;
  background-color: ${({ $bg }) => $bg};
  color: ${({ $fg }) => $fg};
  font-family: ${fonts.detail2Medium.fontFamily};
  font-size: 11px;
  letter-spacing: -0.2px;
`;

const EpayCardText = styled.div`
  flex: 1;
  min-width: 0;
`;

const EpayCardName = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${colors.gray900};
  font-family: ${fonts.body3SemiBold.fontFamily};
  line-height: ${fonts.body3SemiBold.lineHeight};
  font-size: ${fonts.body3SemiBold.fontSize};
`;

const EpayDefaultBadge = styled.span`
  padding: 0 6px;
  border: 1px solid ${colors.blue100};
  border-radius: 4px;
  background-color: ${colors.white};
  color: ${colors.blue600};
  font-family: ${fonts.detail2Medium.fontFamily};
  line-height: 18px;
  font-size: 11px;
`;

const EpayCardMeta = styled.div`
  margin-top: 2px;
  overflow: hidden;
  color: ${colors.gray500};
  font-family: ${fonts.detail2Regular.fontFamily};
  line-height: ${fonts.detail2Regular.lineHeight};
  font-size: ${fonts.detail2Regular.fontSize};
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const EpayCardActions = styled.div`
  display: flex;
  flex: 0 0 auto;
`;

const EpayTextButton = styled.button`
  border: 0;
  padding: 6px;
  background: transparent;
  color: ${colors.gray500};
  font-family: ${fonts.detail2Medium.fontFamily};
  font-size: ${fonts.detail2Medium.fontSize};
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const EpayAddCard = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  width: 100%;
  height: 56px;
  border: 1px dashed ${colors.gray200};
  border-radius: 8px;
  background-color: ${colors.white};
  color: ${colors.gray600};
  font-family: ${fonts.body3Medium.fontFamily};
  line-height: ${fonts.body3Medium.lineHeight};
  font-size: ${fonts.body3Medium.fontSize};
  cursor: pointer;

  span {
    color: ${colors.blue600};
    font-size: 18px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const EpayFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 4px;
`;

const EpayManagementActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  row-gap: 4px;
`;

const EpayManagementButton = styled.button`
  border: 0;
  padding: 4px 0;
  background: transparent;
  color: ${colors.gray600};
  font-family: ${fonts.detail2Medium.fontFamily};
  line-height: ${fonts.detail2Medium.lineHeight};
  font-size: ${fonts.detail2Medium.fontSize};
  cursor: pointer;

  & + &::before {
    content: "";
    display: inline-block;
    width: 1px;
    height: 10px;
    margin: 0 10px;
    background-color: ${colors.gray200};
    vertical-align: -1px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;
